import React, { useState } from 'react';
import {
  Button,
  Input,
  Space,
  Typography,
  message,
  Card,
  Table,
  Tag,
  Checkbox,
  Descriptions,
} from 'antd';
import PageTitle from '../../components/PageTitle';
import {
  adminBluetoothEstimatePreview,
  adminBluetoothExclusivePoolVoiceTest,
  adminBluetoothExclusivePoolVoiceTestCallsToday,
} from '../../api';

const { Text, Paragraph } = Typography;

const voiceAnswerTagColor = (status) => {
  if (status === 'answered') return 'success';
  if (status === 'send_failed' || status === 'query_error') return 'error';
  if (status === 'unanswered') return 'warning';
  return 'default';
};

const BluetoothExclusivePoolVoiceTest = () => {
  const [calledShowNumber, setCalledShowNumber] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [phone, setPhone] = useState('');
  const [ttsCode, setTtsCode] = useState('');
  const [forceCall, setForceCall] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [queryLoading, setQueryLoading] = useState(false);
  const [list, setList] = useState([]);
  const [queryMsg, setQueryMsg] = useState('');

  const loadToday = async (phoneOverride) => {
    const p = String(phoneOverride ?? phone ?? '').trim();
    if (!p) {
      message.warning('请先输入手机号');
      return;
    }
    setQueryLoading(true);
    try {
      const res = await adminBluetoothExclusivePoolVoiceTestCallsToday(p);
      if (res?.success) {
        setList(Array.isArray(res.data?.list) ? res.data.list : []);
        setQueryMsg(res.msg || '');
      } else {
        setList([]);
        message.error(res?.msg || '查询失败');
      }
    } catch (e) {
      setList([]);
      message.error(e.response?.data?.msg || e.message || '查询失败');
    } finally {
      setQueryLoading(false);
    }
  };

  const runPreview = async () => {
    const id = String(deviceId || '').trim();
    if (!id) {
      message.warning('请输入 device_id');
      return;
    }
    setPreviewLoading(true);
    try {
      const res = await adminBluetoothEstimatePreview({ device_id: id });
      if (res?.success) {
        setPreview(res.data || null);
        message.success(res.msg || '预估完成');
      } else {
        setPreview(null);
        message.error(res?.msg || '预估失败');
      }
    } catch (e) {
      setPreview(null);
      message.error(e.response?.data?.msg || e.message || '预估失败');
    } finally {
      setPreviewLoading(false);
    }
  };

  const sendTest = async () => {
    const id = String(deviceId || '').trim();
    const p = String(phone || '').trim();
    const show = String(calledShowNumber || '').trim();
    if (!id) {
      message.warning('请输入 device_id');
      return;
    }
    if (!p) {
      message.warning('请输入手机号');
      return;
    }
    if (!show) {
      message.warning('请输入专属外显号码');
      return;
    }
    setSendLoading(true);
    try {
      const res = await adminBluetoothExclusivePoolVoiceTest({
        device_id: id,
        phone: p,
        tts_code: String(ttsCode || '').trim(),
        called_show_number: show,
        force_call: forceCall,
      });
      if (res?.success) {
        message.success(res.msg || `已发起外呼，fang=${res.data?.fang || ''}`);
        if (res.data) setPreview((prev) => ({ ...(prev || {}), ...res.data }));
        await loadToday(p);
      } else {
        if (res?.data) setPreview(res.data);
        message.warning(res?.msg || '未发起外呼');
        await loadToday(p);
      }
    } catch (e) {
      message.error(e.response?.data?.msg || e.message || '外呼失败');
    } finally {
      setSendLoading(false);
    }
  };

  return (
    <div>
      <PageTitle title="蓝牙专属语音测试" />

      <Card title="蓝牙预估与外呼（仅 fang，无 qian）" size="small" style={{ marginBottom: 16, maxWidth: 820 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text type="secondary">专属外显号码</Text>
            <Input
              value={calledShowNumber}
              onChange={(e) => setCalledShowNumber(e.target.value)}
              placeholder="例如 057112345678"
              disabled={previewLoading || sendLoading}
            />
          </div>
          <div>
            <Text type="secondary">device_id（QK/NQK 蓝牙电表）</Text>
            <Input
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
              placeholder="例如 12345"
              disabled={previewLoading || sendLoading}
            />
          </div>
          <div>
            <Text type="secondary">手机号（测试打给谁就填谁，不与租客绑定）</Text>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="建议填你自己的手机号"
              disabled={previewLoading || sendLoading}
            />
          </div>
          <div>
            <Text type="secondary">蓝牙专属 TTS 模板 ID（可手填；留空则读 voice_notify_tts_code_exclusive_ble）</Text>
            <Input
              value={ttsCode}
              onChange={(e) => setTtsCode(e.target.value)}
              placeholder="TTS_xxx"
              disabled={previewLoading || sendLoading}
            />
          </div>
          <Checkbox checked={forceCall} onChange={(e) => setForceCall(e.target.checked)}>
            强制外呼（未低电也拨打，仅测试话术）
          </Checkbox>
          <Space wrap>
            <Button loading={previewLoading} onClick={runPreview}>
              预估预览
            </Button>
            <Button type="primary" loading={sendLoading} onClick={sendTest}>
              发送蓝牙语音测试
            </Button>
            <Button loading={queryLoading} onClick={() => loadToday()}>
              查询今日结果
            </Button>
          </Space>
        </Space>
      </Card>

      {preview ? (
        <Card title="预估结果" size="small" style={{ marginBottom: 16, maxWidth: 820 }}>
          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label="fang">{preview.fang || '—'}</Descriptions.Item>
            <Descriptions.Item label="是否低电">
              {preview.is_low_balance ? <Tag color="error">是</Tag> : <Tag color="success">否</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="上次抄表时间">{preview.anchor_time || '—'}</Descriptions.Item>
            <Descriptions.Item label="上次抄表剩余">{preview.anchor_remaining ?? '—'} 度</Descriptions.Item>
            <Descriptions.Item label="预估日均">{preview.daily_kwh ?? '—'} 度/天</Descriptions.Item>
            <Descriptions.Item label="预估当前剩余">{preview.estimated_remaining ?? '—'} 度</Descriptions.Item>
            <Descriptions.Item label="低电阈值">{preview.threshold_kwh ?? '—'} 度</Descriptions.Item>
            <Descriptions.Item label="有效段数">{preview.valid_segment_count ?? 0}</Descriptions.Item>
            <Descriptions.Item label="历史记录数">{preview.record_count ?? 0}</Descriptions.Item>
            <Descriptions.Item label="默认6度/天">{preview.used_default_daily ? '是' : '否'}</Descriptions.Item>
          </Descriptions>
          <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0, fontSize: 12 }}>
            蓝牙专属模板只播报 fang，不播报金额。未低电时默认不外呼，可勾选「强制外呼」。
          </Paragraph>
        </Card>
      ) : null}

      <Card title="今日外呼记录" size="small" style={{ maxWidth: 900 }}>
        {queryMsg ? (
          <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
            {queryMsg}
          </Text>
        ) : null}
        <Table
          size="small"
          rowKey={(row) => String(row.queue_id || row.seq)}
          loading={queryLoading}
          dataSource={list}
          pagination={false}
          locale={{ emptyText: queryLoading ? '查询中…' : '今日暂无测试记录' }}
          columns={[
            { title: '序号', dataIndex: 'seq', width: 56 },
            { title: '时间', dataIndex: 'queued_at', width: 150 },
            { title: '房源', dataIndex: 'fang', ellipsis: true },
            { title: '发送', dataIndex: 'queue_status_text', width: 80 },
            {
              title: '接听结果',
              dataIndex: 'answer_label',
              render: (text, row) => (
                <Tag color={voiceAnswerTagColor(row.answer_status)} style={{ margin: 0 }}>
                  {text || '—'}
                </Tag>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default BluetoothExclusivePoolVoiceTest;
